import { z } from "zod";
import { bdMobileSchema } from "./auth";
import { mediaKeySchema } from "./media";

const MAX_MONEY = 10_000_000_000_000; // ৳100 billion in poisha
const MAX_QTY = 1_000_000_000_000; // one billion units, milli

export const poishaSchema = z.number().int().min(0).max(MAX_MONEY);
export const qtySchema = z.number().int().positive().max(MAX_QTY);
export const signedQtySchema = z
  .number()
  .int()
  .min(-MAX_QTY)
  .max(MAX_QTY)
  .refine((v) => v !== 0, "Quantity cannot be zero");
export const rateSchema = z.number().int().min(0).max(10_000);
export const idSchema = z.string().min(1).max(64);
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD");
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional();

export const discountSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("amount"), value: poishaSchema }),
  z.object({ type: z.literal("percent"), value: rateSchema }),
]);

/** A serial or IMEI number, stored upper-case without spaces. */
export const serialSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/\s+/g, "").toUpperCase())
  .pipe(z.string().regex(/^[0-9A-Z\-/.]{4,40}$/, "Serial numbers are 4–40 letters or digits"));
const serialsField = z.array(serialSchema).max(1000).optional();

export const barcodeSchema = z
  .string()
  .trim()
  .regex(/^[0-9A-Za-z\-.]{3,48}$/, "Barcode may contain letters, digits, - and .");

const variantInput = z.object({
  sku: z.string().trim().min(1).max(48).optional(),
  attributes: z.record(z.string().max(32), z.string().max(48)).default({}),
  salePrice: poishaSchema,
  mrp: poishaSchema.nullable().optional(),
  barcodes: z.array(barcodeSchema).max(10).default([]),
});

export const itemUnitSchema = z.object({
  unitCode: z.string().min(1).max(16),
  /** Base units in one of this unit, milli. */
  factor: qtySchema,
  salePrice: poishaSchema.nullable().optional(),
});

const itemFields = {
  nameEn: z.string().trim().max(160).default(""),
  nameBn: z.string().trim().max(160).default(""),
  categoryId: idSchema.nullable().optional(),
  brand: optionalText(80),
  vatRate: rateSchema.default(0),
  sdRate: rateSchema.default(0),
  reorderLevel: z.number().int().min(0).max(MAX_QTY).default(0),
  trackStock: z.boolean().default(true),
  custom: z
    .record(z.string(), z.union([z.string().max(500), z.number(), z.boolean(), z.null()]))
    .default({}),
  /** Uploaded photos, first is the main one (FR-WEB-03). */
  images: z.array(mediaKeySchema).max(6).default([]),
  descriptionEn: z.string().trim().max(4000).default(""),
  descriptionBn: z.string().trim().max(4000).default(""),
  /** Kept off the storefront and the public catalog while still sold in the shop. */
  onlineHidden: z.boolean().default(false),
  trackSerials: z.boolean().default(false),
  warrantyMonths: z.number().int().min(0).max(120).default(0),
};

export const itemCreateSchema = z
  .object({
    ...itemFields,
    baseUnit: z.string().min(1).max(16),
    variants: z.array(variantInput).min(1).max(100),
    units: z.array(itemUnitSchema).max(10).default([]),
    /** Stock the shop has now, recorded with the product in one step (first variant). */
    openingStock: z
      .object({
        branchId: idSchema,
        qty: qtySchema,
        /** Cost per base unit, poisha. */
        unitCost: poishaSchema,
        batchNo: optionalText(40),
        expiryDate: isoDateSchema.nullable().optional(),
      })
      .optional(),
  })
  .refine((v) => v.nameEn !== "" || v.nameBn !== "", { message: "Enter a name", path: ["nameEn"] });
export type ItemCreateInput = z.input<typeof itemCreateSchema>;
export type ItemCreate = z.output<typeof itemCreateSchema>;

export const itemUpdateSchema = z
  .object({
    nameEn: z.string().trim().max(160).optional(),
    nameBn: z.string().trim().max(160).optional(),
    categoryId: idSchema.nullable().optional(),
    brand: optionalText(80),
    vatRate: rateSchema.optional(),
    sdRate: rateSchema.optional(),
    reorderLevel: z.number().int().min(0).max(MAX_QTY).optional(),
    trackStock: z.boolean().optional(),
    custom: itemFields.custom.optional(),
    images: z.array(mediaKeySchema).max(6).optional(),
    descriptionEn: z.string().trim().max(4000).optional(),
    descriptionBn: z.string().trim().max(4000).optional(),
    onlineHidden: z.boolean().optional(),
    trackSerials: z.boolean().optional(),
    warrantyMonths: z.number().int().min(0).max(120).optional(),
    units: z.array(itemUnitSchema).max(10).optional(),
    status: z.enum(["active", "archived"]).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, "Nothing to update");
export type ItemUpdate = z.output<typeof itemUpdateSchema>;

export const variantCreateSchema = variantInput;
export type VariantCreate = z.output<typeof variantCreateSchema>;

export const variantUpdateSchema = z
  .object({
    sku: z.string().trim().min(1).max(48).optional(),
    attributes: z.record(z.string().max(32), z.string().max(48)).optional(),
    salePrice: poishaSchema.optional(),
    mrp: poishaSchema.nullable().optional(),
    barcodes: z.array(barcodeSchema).max(10).optional(),
    status: z.enum(["active", "archived"]).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, "Nothing to update");
export type VariantUpdate = z.output<typeof variantUpdateSchema>;

export const categorySchema = z
  .object({
    nameEn: z.string().trim().max(80).default(""),
    nameBn: z.string().trim().max(80).default(""),
    parentId: idSchema.nullable().optional(),
  })
  .refine((v) => v.nameEn !== "" || v.nameBn !== "", { message: "Enter a name", path: ["nameEn"] });

const lotFields = {
  batchNo: optionalText(40),
  mfgDate: isoDateSchema.nullable().optional(),
  expiryDate: isoDateSchema.nullable().optional(),
};

export const openingStockSchema = z.object({
  branchId: idSchema,
  lines: z
    .array(
      z.object({
        variantId: idSchema,
        qty: qtySchema,
        /** Cost per base unit, poisha. */
        unitCost: poishaSchema,
        ...lotFields,
      }),
    )
    .min(1)
    .max(500),
});
export type OpeningStock = z.output<typeof openingStockSchema>;

export const stockAdjustmentSchema = z.object({
  branchId: idSchema,
  type: z.enum(["adjustment", "damage", "expiry_writeoff"]),
  reason: z.string().trim().min(2).max(200),
  lines: z
    .array(
      z.object({
        variantId: idSchema,
        /** Signed base quantity, milli. Damage and write-offs must be negative. */
        qty: signedQtySchema,
        /** Required for increases: cost per base unit. */
        unitCost: poishaSchema.optional(),
        /** Optional specific lot to reduce (e.g. the expired batch). */
        lotId: idSchema.optional(),
        ...lotFields,
      }),
    )
    .min(1)
    .max(200),
});
export type StockAdjustment = z.output<typeof stockAdjustmentSchema>;

export const customerSchema = z.object({
  name: z.string().trim().min(1).max(120),
  phone: bdMobileSchema.nullable().optional(),
  address: optionalText(240),
  groupName: optionalText(40),
  /** Price list for this customer; null uses normal prices. */
  priceTierId: idSchema.nullable().optional(),
  bin: z
    .string()
    .trim()
    .regex(/^\d{9,13}$/, "BIN must be 9 to 13 digits")
    .nullable()
    .optional(),
  creditLimit: poishaSchema.nullable().optional(),
  openingBalance: z.number().int().min(-MAX_MONEY).max(MAX_MONEY).default(0),
  custom: itemFields.custom,
});
export type CustomerInput = z.output<typeof customerSchema>;
export const customerUpdateSchema = customerSchema
  .omit({ openingBalance: true })
  .partial()
  .extend({ status: z.enum(["active", "archived"]).optional() });

export const supplierSchema = z.object({
  name: z.string().trim().min(1).max(120),
  contactPerson: optionalText(80),
  phone: z.string().trim().max(20).nullable().optional(),
  address: optionalText(240),
  bin: z
    .string()
    .trim()
    .regex(/^\d{9,13}$/)
    .nullable()
    .optional(),
  paymentTermsDays: z.number().int().min(0).max(365).nullable().optional(),
  openingBalance: z.number().int().min(-MAX_MONEY).max(MAX_MONEY).default(0),
  custom: itemFields.custom,
});
export type SupplierInput = z.output<typeof supplierSchema>;
export const supplierUpdateSchema = supplierSchema
  .omit({ openingBalance: true })
  .partial()
  .extend({ status: z.enum(["active", "archived"]).optional() });

export const paymentKindSchema = z.enum([
  "cash",
  "bkash",
  "nagad",
  "rocket",
  "upay",
  "card",
  "bank",
]);

export const paymentAccountSchema = z.object({
  kind: paymentKindSchema,
  name: z.string().trim().min(1).max(60),
  branchId: idSchema.nullable().optional(),
  accountNumber: optionalText(40),
  openingBalance: poishaSchema.default(0),
});
export type PaymentAccountInput = z.output<typeof paymentAccountSchema>;

export const tenderSchema = z.object({
  accountId: idSchema,
  amount: poishaSchema.refine((v) => v > 0, "Amount must be more than zero"),
  txnRef: z.string().trim().min(4).max(40).optional(),
});
export type Tender = z.output<typeof tenderSchema>;

export const purchaseCreateSchema = z.object({
  branchId: idSchema,
  supplierId: idSchema,
  supplierInvoiceNo: optionalText(40),
  supplierInvoiceDate: isoDateSchema.nullable().optional(),
  lines: z
    .array(
      z.object({
        variantId: idSchema,
        unitCode: z.string().min(1).max(16),
        qty: qtySchema,
        /** Cost per entered unit, poisha. */
        unitCost: poishaSchema,
        discount: poishaSchema.default(0),
        vatRate: rateSchema.default(0),
        ...lotFields,
        /** Serial or IMEI numbers received, one per unit (FR-INV-10). */
        serials: serialsField,
      }),
    )
    .min(1)
    .max(500),
  billDiscount: poishaSchema.default(0),
  landedCost: poishaSchema.default(0),
  landedCostAllocation: z.enum(["value", "qty"]).default("value"),
  payments: z.array(tenderSchema).max(5).default([]),
  note: optionalText(500),
});
export type PurchaseCreate = z.output<typeof purchaseCreateSchema>;

export const purchaseReturnSchema = z.object({
  purchaseId: idSchema,
  lines: z
    .array(z.object({ purchaseLineId: idSchema, baseQty: qtySchema }))
    .min(1)
    .max(500),
  refund: tenderSchema.omit({ amount: true }).extend({ amount: poishaSchema }).optional(),
  reason: z.string().trim().min(2).max(200),
});
export type PurchaseReturn = z.output<typeof purchaseReturnSchema>;

export const partyPaymentSchema = z.object({
  branchId: idSchema,
  accountId: idSchema,
  amount: poishaSchema.refine((v) => v > 0, "Amount must be more than zero"),
  txnRef: z.string().trim().min(4).max(40).optional(),
  registerSessionId: idSchema.optional(),
  note: optionalText(200),
});
export type PartyPayment = z.output<typeof partyPaymentSchema>;

export const voucherCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9-]{3,24}$/, "Use 3–24 letters, digits or -");

export const saleCreateSchema = z.object({
  /** Client-generated UUID; resubmitting the same sale returns the original (FR-OFF-03). */
  clientId: z.uuid(),
  branchId: idSchema,
  registerSessionId: idSchema.optional(),
  customerId: idSchema.nullable().optional(),
  /** Sales rep credited with the sale. */
  salesRepId: idSchema.nullable().optional(),
  /** Set by offline clients to the time of sale. */
  soldAt: z.number().int().positive().optional(),
  offline: z.boolean().default(false),
  lines: z
    .array(
      z.object({
        variantId: idSchema,
        unitCode: z.string().min(1).max(16),
        qty: qtySchema,
        /** Price per unit when overridden; otherwise the catalog price is used. */
        unitPrice: poishaSchema.optional(),
        discount: discountSchema.optional(),
        /** Serial or IMEI numbers sold, one per unit, for serial-tracked items. */
        serials: serialsField,
      }),
    )
    .min(1)
    .max(300),
  billDiscount: discountSchema.optional(),
  /** Discount code; checked and applied by the server. */
  voucherCode: voucherCodeSchema.optional(),
  /** Loyalty points the customer spends on this bill. */
  redeemPoints: z.number().int().positive().max(10_000_000).optional(),
  payments: z.array(tenderSchema).max(5).default([]),
  /** Approval for exceeding the credit limit (needs customer.credit.override). */
  creditOverrideReason: z.string().trim().min(3).max(200).optional(),
  note: optionalText(300),
});
export type SaleCreate = z.output<typeof saleCreateSchema>;
export type SaleCreateInput = z.input<typeof saleCreateSchema>;

export const saleReturnSchema = z.object({
  saleId: idSchema,
  lines: z
    .array(z.object({ saleLineId: idSchema, baseQty: qtySchema, serials: serialsField }))
    .min(1)
    .max(300),
  /** Omit to reduce the customer's due instead of refunding (only for named customers). */
  refundAccountId: idSchema.optional(),
  registerSessionId: idSchema.optional(),
  reason: z.string().trim().min(2).max(200),
});
export type SaleReturn = z.output<typeof saleReturnSchema>;

export const saleVoidSchema = z.object({ reason: z.string().trim().min(3).max(200) });

export const registerOpenSchema = z.object({
  branchId: idSchema,
  cashAccountId: idSchema,
  openingFloat: poishaSchema,
});

export const registerCloseSchema = z.object({
  countedCash: poishaSchema,
  note: optionalText(300),
});

export const cashMoveSchema = z.object({
  direction: z.enum(["in", "out"]),
  amount: poishaSchema.refine((v) => v > 0, "Amount must be more than zero"),
  note: z.string().trim().min(2).max(200),
});

export const heldSaleSchema = z.object({
  branchId: idSchema,
  label: z.string().trim().min(1).max(60),
  cart: z.unknown(),
});

export const expenseCategorySchema = z
  .object({
    nameEn: z.string().trim().max(60).default(""),
    nameBn: z.string().trim().max(60).default(""),
  })
  .refine((v) => v.nameEn !== "" || v.nameBn !== "", { message: "Enter a name", path: ["nameEn"] });

export const expenseCreateSchema = z.object({
  branchId: idSchema,
  categoryId: idSchema,
  accountId: idSchema,
  amount: poishaSchema.refine((v) => v > 0, "Amount must be more than zero"),
  at: z.number().int().positive().optional(),
  txnRef: z.string().trim().min(4).max(40).optional(),
  note: optionalText(300),
});
export type ExpenseCreate = z.output<typeof expenseCreateSchema>;

export const accountTransferSchema = z
  .object({
    branchId: idSchema,
    fromAccountId: idSchema,
    toAccountId: idSchema,
    amount: poishaSchema.refine((v) => v > 0, "Amount must be more than zero"),
    fee: poishaSchema.default(0),
    txnRef: z.string().trim().min(4).max(40).optional(),
    note: optionalText(200),
  })
  .refine((v) => v.fromAccountId !== v.toAccountId, {
    message: "Choose two different accounts",
    path: ["toAccountId"],
  });
export type AccountTransfer = z.output<typeof accountTransferSchema>;

export const ownerMoneySchema = z.object({
  branchId: idSchema,
  accountId: idSchema,
  direction: z.enum(["capital", "draw"]),
  amount: poishaSchema.refine((v) => v > 0, "Amount must be more than zero"),
  note: optionalText(200),
});

export const branchInputSchema = z.object({
  name: z.string().trim().min(2).max(80),
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{2,6}$/, "2–6 letters or digits"),
  phone: z.string().trim().max(20).nullable().optional(),
  address: optionalText(200),
});
export type BranchInput = z.output<typeof branchInputSchema>;

/** One row of an item import (FR-IO-01): an item with optional opening stock. */
export const importItemSchema = z
  .object({
    nameEn: z.string().trim().max(160).default(""),
    nameBn: z.string().trim().max(160).default(""),
    category: z.string().trim().max(80).optional(),
    brand: z.string().trim().max(80).optional(),
    baseUnit: z.string().trim().min(1).max(16),
    salePrice: poishaSchema,
    mrp: poishaSchema.nullable().optional(),
    sku: z.string().trim().min(1).max(48).optional(),
    barcode: barcodeSchema.optional(),
    vatRate: rateSchema.default(0),
    reorderLevel: z.number().int().min(0).max(MAX_QTY).default(0),
    openingQty: z.number().int().min(0).max(MAX_QTY).default(0),
    openingCost: poishaSchema.default(0),
    batchNo: z.string().trim().max(40).optional(),
    expiryDate: isoDateSchema.optional(),
    custom: z.record(z.string(), z.string().max(500)).default({}),
  })
  .refine((v) => v.nameEn !== "" || v.nameBn !== "", { message: "Enter a name", path: ["nameEn"] });
export type ImportItem = z.output<typeof importItemSchema>;

export const importItemsSchema = z.object({
  branchId: idSchema,
  rows: z.array(importItemSchema).min(1).max(2000),
});

export const importPartiesSchema = z.object({
  rows: z
    .array(
      z.object({
        name: z.string().trim().min(1).max(120),
        phone: z.string().trim().max(20).optional(),
        address: z.string().trim().max(240).optional(),
        openingBalance: z.number().int().min(-MAX_MONEY).max(MAX_MONEY).default(0),
      }),
    )
    .min(1)
    .max(5000),
});

/** Serials recorded on a sale after it was made (online orders, offline sales). */
export const saleSerialsSchema = z.object({
  lines: z
    .array(z.object({ saleLineId: idSchema, serials: z.array(serialSchema).min(1).max(1000) }))
    .min(1)
    .max(300),
});
